import { useState, useEffect, useRef } from "react";
import { useTheme } from "../ThemeContext";
import { Card } from "../ui/card";
import { Badge } from "../ui/badge";
import { User, PlayCircle, Info, Sparkles, ArrowLeft, ArrowRight } from "lucide-react";
import ShinyText from "../reactbits/ShinyText";
import { motion, AnimatePresence } from "motion/react";
import PhoneMockup from "../PhoneMockup";
import { DiaTextReveal } from "../ui/dia-text-reveal";
import { Highlighter } from "../ui/highlighter";
import {
    Carousel,
    CarouselContent,
    CarouselItem,
    CarouselNext,
    CarouselPrevious,
    type CarouselApi,
} from "../ui/carousel";

// Videos: each one is picked to fit the persona it sits next to
import { clipByTitle } from "../../config/providerVideos";
import video3 from "../../assets/retreat_full_1.mp4";

const videoSarah = clipByTitle("Evening Wind-Down").src; // illustrated woman winding down on the sofa at night
const videoMarcus = clipByTitle("Mindful Commute").src; // line-art man taking a short pause in a busy day
const videoChloe = clipByTitle("Not Alone").src; // gentle illustrated waiting room, a calm way to observe others

// Personas Images
import imgSarah from "../../assets/personas/persona_sarah_1776868588085.png";
import imgMarcus from "../../assets/personas/persona_marcus_1776868613715.png";
import imgElena from "../../assets/personas/persona_elena_1776868629815.png";
import imgDavid from "../../assets/personas/persona_david_1776868647449.png";
import imgChloe from "../../assets/personas/persona_chloe_1776868663740.png";

const personas = [
    {
        id: "sarah",
        name: "Sarah Jenkins",
        profilePic: imgSarah,
        ageGroup: "25",
        clinicalTags: ["Insomnia", "Anxiety"], // objective demographics
        personalityTags: ["Overthinker", "Visual Learner", "Detail-Oriented", "Quiet", "Introspective", "Planner"], // preferences/likes, tastes, motives, ... // TODO : add more tags
        visualTags: ["style_realistic", "soothing_colors"],
        messagingTags: ["gentle", "reassuring", "validating"],
        soundTags: ["female_voice", "slow_paced_narration", "lo-fi_music", "asmr_elements"],
        pastPreferences: [
            "Liked videos of rain on windows",
            "Prefers soft female voiceovers",
            "Saved 10-minute guided meditations",
            "Dislikes sudden loud noises",
            "Usually watches before bed"
        ],
        goal: "Improve sleep quality",
        video: videoSarah,
        rationale: (
            <>
                Sarah is drawn to <Highlighter color="#c084fc8b" action="underline">visual, soothing routines</Highlighter>. This sensory-rich video was generated to gently guide her mind away from overthinking and into <Highlighter color="#84e4fc30" action="circle">relaxation</Highlighter>.
            </>
        )
    },
    {
        id: "marcus",
        name: "Marcus Cole",
        profilePic: imgMarcus,
        ageGroup: "43",
        clinicalTags: ["High Stress", "Hypertension"],
        personalityTags: ["Analytical", "Busy", "Pragmatic", "Goal-Oriented", "Direct", "Impatient"],
        visualTags: ["direct", "logical_flow", "clear_actions"],
        messagingTags: ["direct", "action_oriented", "concise"],
        soundTags: ["confident_male", "upbeat", "energetic_tempo", "no_bg_music"],
        pastPreferences: [
            "Liked 60-second productivity tips",
            "Always skips long intros",
            "Saved desk stretching routines",
            "Prefers no background music",
            "Engages with stat-driven facts"
        ],
        goal: "Incorporate micro-breaks for stress",
        video: videoMarcus,
        rationale: (
            <>
                Marcus needs <Highlighter color="#38bdf830">quick, actionable interventions</Highlighter>. This direct video matches his <Highlighter color="#38bdf830">analytical side</Highlighter>, capturing attention without long time commitments.
            </>
        )
    },
    {
        id: "elena",
        name: "Elena Rodriguez",
        profilePic: imgElena,
        ageGroup: "61",
        clinicalTags: ["Chronic Pain", "Mobility Limits"],
        personalityTags: ["Nature Lover", "Spiritual", "Gentle", "Reflective", "Patient", "Empathetic"],
        visualTags: ["nature_themed", "expansive", "calm_pacing"],
        messagingTags: ["empathetic", "grounding", "respectful"],
        soundTags: ["warm_female", "measured_breath", "nature_soundscape", "bowls"],
        pastPreferences: [
            "Saved videos about gardening",
            "Liked videos with golden retrievers",
            "Prefers slow, guided breathing",
            "Often watches early morning",
            "Enjoyed forest soundscapes"
        ],
        goal: "Manage chronic pain",
        video: video3,
        rationale: (
            <>
                Elena responds well to <Highlighter color="#4ade8030">natural, expansive environments</Highlighter>. This retreat was crafted to create an <Highlighter color="#4ade8030">immersive, pain-free mental space</Highlighter>.
            </>
        )
    },
    {
        id: "david",
        name: "David Chen",
        profilePic: imgDavid,
        ageGroup: "18",
        clinicalTags: ["ADHD", "Burnout"],
        personalityTags: ["High-Energy", "Gamer", "Competitive", "Easily Distracted", "Tech-Savvy", "Spontaneous"],
        visualTags: ["gamified", "dynamic", "high_stimulation"],
        messagingTags: ["engaging", "challenge_based", "dynamic"],
        soundTags: ["energetic_male", "fast_paced", "electronic_synth", "audio_cues"],
        pastPreferences: [
            "Liked fast-paced gaming clips",
            "Watched satisfying looping animations",
            "Skips slow-paced content",
            "Prefers electronic/synth backgrounds",
            "Saved focus-timer challenges"
        ],
        goal: "Regain focus & build a routine",
        hidden: true, // no video yet
        rationale: (
            <>
                David thrives on <Highlighter color="#fbbf2430">high stimulation</Highlighter>. This dynamic sequence provides enough sensory input to <Highlighter color="#fbbf2430">hold his focus</Highlighter> while quietly teaching pacing.
            </>
        )
    },
    {
        id: "chloe",
        name: "Chloe Miller",
        profilePic: imgChloe,
        ageGroup: "22",
        clinicalTags: ["Social Anxiety", "Self-Doubt"],
        personalityTags: ["Creative", "Introverted", "Imaginative", "Observant", "Empathetic", "Cautious"],
        visualTags: ["abstract", "character_driven", "gentle"],
        messagingTags: ["story_driven", "encouraging", "soft_spoken"],
        soundTags: ["non_binary_voice", "gentle_melody", "watercolor_foley", "soft_reverb"],
        pastPreferences: [
            "Liked videos with cats in them",
            "Saved watercolor painting tutorials",
            "Prefers muted, pastel color palettes",
            "Often re-watches comforting content",
            "Engaged with 'day in the life' vlogs"
        ],
        goal: "Relieve social anxiety",
        video: videoChloe,
        rationale: (
            <>
                Chloe is <Highlighter color="#f472b630">highly imaginative</Highlighter>. This narrative avoids direct confrontation, allowing her to <Highlighter color="#f472b630">comfortably observe interactions</Highlighter> and build confidence.
            </>
        )
    }
];

const visiblePersonas = personas.filter((p) => !("hidden" in p && p.hidden));

/** True from the md breakpoint up; used so only the video layout that is visible gets mounted (and downloaded). */
function useIsDesktop() {
    const [desktop, setDesktop] = useState(() => typeof window !== "undefined" && window.matchMedia("(min-width: 768px)").matches);
    useEffect(() => {
        const mq = window.matchMedia("(min-width: 768px)");
        const update = () => setDesktop(mq.matches);
        update();
        mq.addEventListener("change", update);
        return () => mq.removeEventListener("change", update);
    }, []);
    return desktop;
}

/** A looping muted clip that only downloads and plays while its persona is the one in focus. */
function PersonaVideo({ src, active, className, hidden }: { src: string; active: boolean; className?: string; hidden?: boolean }) {
    const ref = useRef<HTMLVideoElement>(null);

    useEffect(() => {
        const video = ref.current;
        if (!video) return;
        if (active) video.play().catch(() => { /* autoplay blocked: the first frame stays up */ });
        else video.pause();
    }, [active]);

    return (
        <video
            ref={ref}
            loop
            muted
            playsInline
            preload={active ? "auto" : "none"}
            aria-hidden={hidden ? true : undefined}
            className={className}
        >
            <source src={src} type="video/mp4" />
        </video>
    );
}

const RotatingPreferences = ({ preferences, theme }: { preferences: string[], theme: string }) => {
    const [index, setIndex] = useState(0);

    useEffect(() => {
        const timer = setInterval(() => {
            setIndex((prev) => (prev - 1 + preferences.length) % preferences.length);
        }, 4000);
        return () => clearInterval(timer);
    }, [preferences.length]);

    const topIndex = index;
    const bottomIndex = (index + 1) % preferences.length;

    const visible = [
        { id: `pref-${topIndex}`, text: preferences[topIndex] },
        { id: `pref-${bottomIndex}`, text: preferences[bottomIndex] }
    ];

    return (
        <div className="flex flex-col gap-1.5 h-[76px] overflow-hidden relative w-full">
            <AnimatePresence mode="popLayout" initial={false}>
                {visible.map((item) => (
                    <motion.div
                        key={item.id}
                        layout
                        initial={{ opacity: 0, y: -20, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 20, scale: 0.95 }}
                        transition={{ duration: 0.4 }}
                        className={`flex items-center gap-2 text-xs px-3 py-2 rounded-md border w-full shrink-0 ${theme === "dark" ? "bg-slate-800/50 border-slate-700 text-slate-300" : "bg-slate-50 border-slate-200 text-slate-700"}`}
                    >
                        <Info className="w-3.5 h-3.5 text-purple-500/80 shrink-0" />
                        <div className="truncate w-full font-medium">
                            <DiaTextReveal text={item.text} textColor={theme === "dark" ? "#cbd5e1" : "#334155"} />
                        </div>
                    </motion.div>
                ))}
            </AnimatePresence>
        </div>
    );
};

export function SentimentSandbox() {
    const { theme } = useTheme();
    const isDesktop = useIsDesktop();
    const [api, setApi] = useState<CarouselApi>();
    const [current, setCurrent] = useState(0);

    useEffect(() => {
        if (!api) return;

        setCurrent(api.selectedScrollSnap());

        api.on("select", () => {
            setCurrent(api.selectedScrollSnap());
        });
    }, [api]);

    return (
        <div className="w-full max-w-7xl mx-auto py-8 md:py-16 overflow-hidden">
            <div className="mb-5 md:mb-12 text-center max-w-3xl mx-auto px-4">
                <Badge variant="outline" className="mb-3 md:mb-4 px-4 py-2 bg-purple-500/10 border-purple-500/30">
                    <User className="w-4 h-4 mr-2 text-purple-500" />
                    <ShinyText text="Hyper-Personalization" speed={3} className="font-medium" />
                </Badge>
                <h3 className={`text-3xl md:text-5xl font-bold mb-3 md:mb-6 ${theme === "dark" ? "text-white" : "text-slate-900"}`}>
                    Your Patients Are Unique. Their Content Should be Too.
                </h3>
                <p className={`text-base md:text-lg ${theme === "dark" ? "text-slate-400" : "text-slate-600"}`}>
                    Swipe through patient profiles to see how clinical goals, engagement history, and individual context shape the resulting therapeutic content.
                </p>
            </div>

            <div className="relative w-full">
                {/* Left Overlay / Prev Button */}
                <div
                    onClick={() => api?.scrollPrev()}
                    className={`pointer-events-none md:pointer-events-auto absolute top-0 bottom-0 -left-3 md:left-0 w-16 md:w-32 lg:w-48 z-20 flex items-center justify-start pl-1 md:pl-8 group md:bg-gradient-to-r ${theme === "dark" ? "md:from-[#0a0a0a] md:via-[#0a0a0a]/80" : "md:from-slate-50 md:via-slate-50/80"} md:to-transparent cursor-pointer`}
                >
                    <div className="pointer-events-auto w-9 h-9 md:w-12 md:h-12 flex items-center justify-center rounded-full border border-purple-500/30 text-purple-500 bg-white/50 dark:bg-black/50 backdrop-blur-md group-hover:scale-110 group-hover:bg-purple-500/20 group-hover:text-purple-600 dark:group-hover:text-purple-400 transition-all shadow-lg">
                        <ArrowLeft className="w-5 h-5 md:w-6 md:h-6" />
                    </div>
                </div>

                {/* Right Overlay / Next Button */}
                <div
                    onClick={() => api?.scrollNext()}
                    className={`pointer-events-none md:pointer-events-auto absolute top-0 bottom-0 -right-3 md:right-0 w-16 md:w-32 lg:w-48 z-20 flex items-center justify-end pr-1 md:pr-8 group md:bg-gradient-to-l ${theme === "dark" ? "md:from-[#0a0a0a] md:via-[#0a0a0a]/80" : "md:from-slate-50 md:via-slate-50/80"} md:to-transparent cursor-pointer`}
                >
                    <div className="pointer-events-auto w-9 h-9 md:w-12 md:h-12 flex items-center justify-center rounded-full border border-purple-500/30 text-purple-500 bg-white/50 dark:bg-black/50 backdrop-blur-md group-hover:scale-110 group-hover:bg-purple-500/20 group-hover:text-purple-600 dark:group-hover:text-purple-400 transition-all shadow-lg">
                        <ArrowRight className="w-5 h-5 md:w-6 md:h-6" />
                    </div>
                </div>

                <Carousel
                    setApi={setApi}
                    opts={{
                        align: "center",
                        loop: true,
                    }}
                    className="w-full"
                >
                    <CarouselContent className="-ml-4 md:-ml-8 items-stretch py-2 md:py-4">
                        {visiblePersonas.map((persona, index) => {
                            const isActive = index === current;
                            return (
                                <CarouselItem key={persona.id} className="pl-4 md:pl-8 basis-[84%] md:basis-[75%] lg:basis-[60%]">
                                    <motion.div
                                        animate={{
                                            scale: isActive ? 1 : 0.9,
                                            opacity: isActive ? 1 : 0.5,
                                        }}
                                        transition={{ duration: 0.4, ease: "easeOut" }}
                                        whileHover={isActive ? { scale: 0.99 } : { scale: 0.92 }}
                                        className={`h-full flex flex-col md:flex-row gap-3 md:gap-8 p-4 md:p-8 rounded-3xl border transition-colors ${theme === "dark"
                                            ? isActive ? "bg-slate-900/80 border-purple-500/40 shadow-lg shadow-purple-500/10" : "bg-slate-900/40 border-purple-500/10"
                                            : isActive ? "bg-white border-purple-300 shadow-xl shadow-purple-500/10" : "bg-white/60 border-purple-100"
                                            }`}
                                    >
                                        {/* Video Column */}
                                        {persona.video && isDesktop && (
                                        <div className="flex flex-shrink-0 w-[280px] justify-center items-center">
                                            <PhoneMockup showCursor={false} className="transform scale-[0.8] md:scale-90 origin-center">
                                                <PersonaVideo src={persona.video} active={isActive} className="absolute inset-0 w-full h-full object-cover" />
                                            </PhoneMockup>
                                        </div>
                                        )}

                                        {/* Content Column */}
                                        <div className="flex-1 flex flex-col justify-center md:py-4 min-w-0">
                                            {/* Persona Header */}
                                            <div className="flex items-center gap-3 md:gap-4 mb-3 pb-3 md:mb-6 md:pb-6 border-b border-purple-500/10">
                                                {persona.video && !isDesktop && <PersonaVideo
                                                    src={persona.video}
                                                    active={isActive}
                                                    hidden
                                                    className="h-[120px] w-[68px] shrink-0 rounded-xl object-cover border border-purple-500/20"
                                                />}
                                                <img src={persona.profilePic} alt={persona.name} className={`${persona.video && !isDesktop ? "hidden" : ""} w-16 h-16 rounded-full object-cover shadow-sm shrink-0 border-2 border-purple-500/30`} />
                                                <div>
                                                    <h4 className={`text-xl md:text-2xl font-bold ${theme === "dark" ? "text-white" : "text-slate-900"}`}>
                                                        {persona.name}
                                                    </h4>
                                                    <p className={`text-sm ${theme === "dark" ? "text-slate-400" : "text-slate-500"}`}>
                                                        Age {persona.ageGroup} • {persona.goal}
                                                    </p>
                                                </div>
                                            </div>

                                            {/* Tags Grid */}
                                            <div className="flex flex-col gap-4 mb-3 md:mb-5 w-full">
                                                <div className="flex items-center gap-2 w-full overflow-hidden">
                                                    <div className="relative w-full" style={{ maskImage: 'linear-gradient(to right, transparent, black 5%, black 95%, transparent)', WebkitMaskImage: 'linear-gradient(to right, transparent, black 5%, black 95%, transparent)' }}>
                                                        <motion.div
                                                            animate={{ x: ["0%", "-50%"] }}
                                                            transition={{ repeat: Infinity, ease: "linear", duration: 25 }}
                                                            className="flex w-max"
                                                        >
                                                            {[0, 1].map((setIndex) => (
                                                                <div key={setIndex} className="flex gap-1.5 pr-1.5 items-center">
                                                                    {persona.clinicalTags.map(tag => (
                                                                        <span key={`clinical-${tag}`} className={`whitespace-nowrap text-xs px-2 py-0.5 rounded-md ${theme === "dark" ? "bg-red-500/10 text-red-400 border border-red-500/20" : "bg-red-50 text-red-700 border border-red-200"}`}>
                                                                            {tag}
                                                                        </span>
                                                                    ))}
                                                                    {persona.personalityTags.map(tag => (
                                                                        <span key={`personality-${tag}`} className={`whitespace-nowrap text-xs px-2 py-0.5 rounded-md ${theme === "dark" ? "bg-blue-500/10 text-blue-400 border border-blue-500/20" : "bg-blue-50 text-blue-700 border border-blue-200"}`}>
                                                                            {tag}
                                                                        </span>
                                                                    ))}
                                                                </div>
                                                            ))}
                                                        </motion.div>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Past Preferences */}
                                            <div className="mb-3 md:mb-6">
                                                <RotatingPreferences preferences={persona.pastPreferences} theme={theme} />
                                            </div>

                                            {/* Rationale & Visual Tags - Simplified Modern Design */}
                                            <div className={`mt-auto p-4 md:p-5 rounded-2xl ${theme === "dark" ? "bg-white/5" : "bg-black/5"}`}>
                                                <div className="flex items-center gap-2 mb-3">
                                                    <Sparkles className={`w-4 h-4 ${theme === "dark" ? "text-white/25" : "text-purple-600"}`} />
                                                    <h5 className={`font-medium text-sm ${theme === "dark" ? "text-white/25" : "text-slate-900"}`}>
                                                        Why this generation?
                                                    </h5>
                                                </div>
                                                <p className={`text-sm leading-relaxed mb-3 md:mb-4 ${theme === "dark" ? "text-slate-300" : "text-slate-600"}`}>
                                                    {persona.rationale}
                                                </p>

                                                <div className="space-y-3">
                                                    <div className="flex flex-col md:flex-row items-start gap-1 md:gap-2">
                                                        <span className={`text-[11px] uppercase font-semibold md:mt-1 shrink-0 md:w-24 ${theme === "dark" ? "text-slate-500" : "text-slate-400"}`}>
                                                            Visual Style
                                                        </span>
                                                        <div className="flex flex-wrap gap-1.5">
                                                            {persona.visualTags.map(tag => (
                                                                <span key={tag} className={`text-xs px-2 py-0.5 rounded-md font-medium ${theme === "dark" ? "bg-purple-500/20 text-purple-300" : "bg-purple-100 text-purple-700"}`}>
                                                                    #{tag}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    </div>

                                                    <div className="flex flex-col md:flex-row items-start gap-1 md:gap-2">
                                                        <span className={`text-[11px] uppercase font-semibold md:mt-1 shrink-0 md:w-24 ${theme === "dark" ? "text-slate-500" : "text-slate-400"}`}>
                                                            Messaging
                                                        </span>
                                                        <div className="flex flex-wrap gap-1.5">
                                                            {persona.messagingTags.map(tag => (
                                                                <span key={tag} className={`text-xs px-2 py-0.5 rounded-md font-medium ${theme === "dark" ? "bg-blue-500/20 text-blue-300" : "bg-blue-100 text-blue-700"}`}>
                                                                    #{tag}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    </div>

                                                    <div className="flex flex-col md:flex-row items-start gap-1 md:gap-2">
                                                        <span className={`text-[11px] uppercase font-semibold md:mt-1 shrink-0 md:w-24 ${theme === "dark" ? "text-slate-500" : "text-slate-400"}`}>
                                                            Sound
                                                        </span>
                                                        <div className="flex flex-wrap gap-1.5">
                                                            {persona.soundTags.map(tag => (
                                                                <span key={tag} className={`text-xs px-2 py-0.5 rounded-md font-medium ${theme === "dark" ? "bg-green-500/20 text-green-300" : "bg-green-100 text-green-700"}`}>
                                                                    #{tag}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </motion.div>
                                </CarouselItem>
                            );
                        })}
                    </CarouselContent>

                </Carousel>
            </div>
        </div>
    );
}

export default SentimentSandbox;
